if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.isVerticalScroll = this.classList.contains('vertical-scroll') && window.innerWidth >= 1025 && !this.closest('.quick-add-modal');

        this.initVariantMedia();
        this.initLoop();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.isVerticalScroll) this.removeListSemantic();
      }

      // Loop: next on the last slide jumps to the first, prev on the first jumps to the last.
      initLoop() {
        [this.elements.viewer, this.elements.thumbnails].forEach((component) => {
          if (!component) return;

          customElements.whenDefined('slider-component').then(() => {
            component.enableSliderLooping = true;
            component.prevButton?.removeAttribute('disabled');
            component.nextButton?.removeAttribute('disabled');
          });

          // Capture phase so this runs before the slider's own button handler.
          component.addEventListener(
            'click',
            (event) => {
              const button = event.target.closest('button[name="previous"], button[name="next"]');
              const items = component.sliderItemsToShow;
              if (!button || !component.slider || !items || items.length < 2) return;

              const step = Math.min(Number(button.dataset.step) || 1, items.length - 1);
              const offset = component.sliderItemOffset;
              if (!offset) return;

              const slider = component.slider;
              const prop = component.isVerticalScroll ? 'scrollTop' : 'scrollLeft';
              const axis = component.isVerticalScroll ? 'top' : 'left';
              const jump = (value) => slider.scrollTo({ [axis]: value, behavior: 'instant' });
              const glide = (value) => requestAnimationFrame(() => slider.scrollTo({ [axis]: value, behavior: 'smooth' }));

              // Infinite: move slides from one end to the other, then compensate the scroll position
              // instantly (looks identical) and glide on, so the wrap-around is seamless.
              if (button.name === 'next' && component.isSlideVisible(items[items.length - 1])) {
                event.preventDefault();
                event.stopImmediatePropagation();
                items.slice(0, step).forEach((li) => slider.appendChild(li));
                component.resetPages();
                const base = slider[prop] - step * offset;
                jump(base);
                glide(base + step * offset);
              } else if (button.name === 'previous' && slider[prop] <= 1) {
                event.preventDefault();
                event.stopImmediatePropagation();
                const firstItem = items[0];
                items.slice(-step).forEach((li) => slider.insertBefore(li, firstItem));
                component.resetPages();
                jump(step * offset);
                glide(0);
              }
            },
            true
          );

          // Scroll / swipe: once scrolling settles at either end, move a slide from the opposite end
          // (scroll position compensated instantly) so there is always a neighbour to scroll into.
          const ul = component.querySelector('ul');
          if (!ul) return;
          let settleTimer;
          ul.addEventListener(
            'scroll',
            () => {
              clearTimeout(settleTimer);
              settleTimer = setTimeout(() => this.rotateAtEdge(component, ul), 150);
            },
            { passive: true }
          );
        });

        this.initStepGestures();

        // Pre-position a neighbour on both sides right after layout, so the very first swipe (back from
        // slide 1, or on to the end) already loops without needing an earlier scroll event.
        requestAnimationFrame(() => this.bufferLoop());
        window.addEventListener('load', () => this.bufferLoop(), { once: true });
        window.addEventListener('resize', debounce(() => this.bufferLoop(), 300));
      }

      bufferLoop() {
        [this.elements.viewer, this.elements.thumbnails].forEach((component) => {
          const ul = component && component.querySelector('ul');
          if (ul) this.rotateAtEdge(component, ul);
        });
      }

      rotateAtEdge(component, ul) {
        const items = Array.from(ul.children).filter((li) => li.tagName === 'LI' && li.getClientRects().length);
        if (items.length < 3) return;

        const vertical = !!component.isVerticalScroll;
        const prop = vertical ? 'scrollTop' : 'scrollLeft';
        const axis = vertical ? 'top' : 'left';
        const offset = vertical
          ? items[1].offsetTop - items[0].offsetTop
          : items[1].offsetLeft - items[0].offsetLeft;
        if (offset <= 0) return;

        const range = vertical ? ul.scrollHeight - ul.clientHeight : ul.scrollWidth - ul.clientWidth;
        // Needs at least two slides of travel, otherwise rotating would flip-flop between the ends.
        if (range < offset * 2 - 2) return;

        const position = ul[prop];
        const jump = (value) => ul.scrollTo({ [axis]: value, behavior: 'instant' });

        if (position <= 2) {
          ul.insertBefore(items[items.length - 1], items[0]);
          jump(offset);
        } else if (position >= range - 2) {
          ul.appendChild(items[0]);
          jump(position - offset);
        } else {
          return;
        }
        if (component.resetPages) component.resetPages();
      }

      // Desktop shows one slide at a time (no native scroll), so horizontal trackpad scroll and
      // touch swipes step to the next / previous slide, wrapping around.
      initStepGestures() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        let wheelLocked = false;
        viewer.addEventListener(
          'wheel',
          (event) => {
            if (window.innerWidth < 750) return;
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 20) return;
            event.preventDefault();
            if (wheelLocked) return;
            wheelLocked = true;
            this.stepActive(event.deltaX > 0 ? 1 : -1);
            setTimeout(() => (wheelLocked = false), 450);
          },
          { passive: false }
        );

        let startX = 0;
        let startY = 0;
        viewer.addEventListener(
          'touchstart',
          (event) => {
            startX = event.changedTouches[0].clientX;
            startY = event.changedTouches[0].clientY;
          },
          { passive: true }
        );
        viewer.addEventListener(
          'touchend',
          (event) => {
            if (window.innerWidth < 750) return;
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            this.stepActive(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }

      stepActive(direction) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (li) => li.tagName === 'LI' && !li.classList.contains('is-variant-hidden')
        );
        if (slides.length < 2) return;
        const current = slides.findIndex((li) => li.classList.contains('is-active'));
        const next = slides[(current + direction + slides.length) % slides.length];
        this.setActiveMedia(next.dataset.mediaId);
      }

      // Variant-wise media: custom.variant_images map (variantId -> ordered media keys) is printed by the snippet.
      initVariantMedia() {
        const mapEl = this.querySelector('[id^="VariantMediaMap"]');
        this.variantMediaMap = {};
        if (!mapEl) return;
        try {
          this.variantMediaMap = JSON.parse(mapEl.textContent) || {};
        } catch (e) {
          return;
        }
        this.applyVariant(mapEl.dataset.selectedVariant, { initial: true });
      }

      // Shows only the variant's own media in metafield order. Returns true when a filter was applied.
      applyVariant(variantId, { initial = false } = {}) {
        const keys = this.variantMediaMap && this.variantMediaMap[variantId];
        const filtered = Array.isArray(keys) && keys.length > 0;

        const lists = [
          [this.elements.viewer, (li) => li.dataset.mediaId],
          [this.elements.thumbnails, (li) => li.dataset.target],
        ];

        lists.forEach(([container, getKey]) => {
          const ul = container && container.querySelector('ul');
          if (!ul) return;
          const items = Array.from(ul.children).filter((li) => li.tagName === 'LI' && getKey(li));
          items.forEach((li, index) => {
            if (li.dataset.originalIndex === undefined) li.dataset.originalIndex = index;
          });

          // No variant_images: leave the gallery exactly as the theme renders it (only undo an earlier filter).
          if (!filtered) {
            items.forEach((li) => li.classList.remove('is-variant-hidden'));
            if (this.wasFiltered && container !== this.elements.viewer) {
              items
                .sort((a, b) => a.dataset.originalIndex - b.dataset.originalIndex)
                .forEach((li) => ul.appendChild(li));
            }
            if (container.resetPages) container.resetPages();
            return;
          }

          const sorted = items.slice().sort((a, b) => {
            if (filtered) {
              const ai = keys.indexOf(getKey(a));
              const bi = keys.indexOf(getKey(b));
              if (ai === -1 && bi === -1) return a.dataset.originalIndex - b.dataset.originalIndex;
              if (ai === -1) return 1;
              if (bi === -1) return -1;
              return ai - bi;
            }
            return a.dataset.originalIndex - b.dataset.originalIndex;
          });

          sorted.forEach((li) => {
            li.classList.toggle('is-variant-hidden', filtered && !keys.includes(getKey(li)));
            ul.appendChild(li);
          });

          if (container.resetPages) container.resetPages();
        });

        this.wasFiltered = filtered;
        if (!filtered) {
          if (!initial) setTimeout(() => this.bufferLoop(), 400);
          return false;
        }

        const firstVisible = this.elements.viewer.querySelector('[data-media-id]:not(.is-variant-hidden)');
        if (!firstVisible) return true;

        if (initial) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
          firstVisible.classList.add('is-active');
          if (this.elements.thumbnails) {
            const thumb = this.elements.thumbnails.querySelector(`[data-target="${firstVisible.dataset.mediaId}"]`);
            this.elements.thumbnails.querySelectorAll('button').forEach((b) => b.removeAttribute('aria-current'));
            thumb?.querySelector('button')?.setAttribute('aria-current', true);
          }
        } else {
          this.elements.viewer.querySelector('ul').scrollTo({ top: 0, left: 0 });
          this.elements.thumbnails?.querySelector('ul')?.scrollTo({ top: 0, left: 0 });
          this.setActiveMedia(firstVisible.dataset.mediaId);
        }
        setTimeout(() => this.bufferLoop(), 400);
        return true;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) || this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.isVerticalScroll) {
            activeMedia.parentElement.scrollTo({ top: activeMedia.offsetTop });
          } else {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
          
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.isVerticalScroll) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}